import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { supabase } from '../../lib/supabaseClient';
import Spinner from '../Atoms/Spinner';

const EVENT_ID = '67590da5499bcc995c210b40';
const MULTI_PIECE_PROJECT_IDS = [
    '682b5c5f754f51df2ef93bbc', // Sam Baron – ODE TO NATURE
    '67e51ddb43d5609350e33667', // Constance Guisset – THE ÉCOLE DES ARTS JOAILLIERS
    '67e56ab043d5609350e3490f', // Friedmann & Versace – CARTIER BOUTIQUE
    '67da910a43d5609350e274d7', // Maxime d'Angeac – ORIENT EXPRESS CORINTHIAN
    '67e6dfff43d5609350e38900', // Rinck – OPUS MEMORIA
];

// ─── Styled Components ───────────────────────────────────────
const PageContainer = styled.div`
    max-width: 720px;
    margin: 0 auto;
    padding: 40px 20px 80px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const LogoImg = styled.img`
    display: block;
    margin: 0 auto 20px;
    max-width: 180px;
`;

const PageTitle = styled.h1`
    text-align: center;
    font-size: 1.6rem;
    color: #1a1a2e;
    margin-bottom: 8px;
    font-weight: 700;
`;

const PageSubtitle = styled.p`
    text-align: center;
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 32px;
`;

const DesignerBadge = styled.div`
    text-align: center;
    font-size: 1.1rem;
    font-weight: 600;
    color: rgb(24, 51, 189);
    background: #f0f4ff;
    border: 1px solid #d0d8ff;
    border-radius: 8px;
    padding: 12px 20px;
    margin-bottom: 28px;
`;

const SectionCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 24px;
    margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
    font-size: 1.1rem;
    color: rgb(24, 51, 189);
    margin-bottom: 20px;
    font-weight: 700;
    border-bottom: 2px solid rgb(24, 51, 189);
    padding-bottom: 8px;
`;

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;

    label {
        font-size: 13px;
        font-weight: 600;
        color: #555;
        margin-bottom: 4px;
    }

    input, textarea, select {
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 14px;
        font-family: inherit;
        transition: border-color 0.2s;
        background: white;

        &:focus {
            outline: none;
            border-color: rgb(24, 51, 189);
        }
    }

    textarea {
        resize: vertical;
        min-height: 60px;
    }
`;

const RequiredMark = styled.span`
    color: #dc3545;
    margin-left: 3px;
`;

const RadioGroup = styled.div`
    display: flex;
    gap: 20px;
    margin-top: 4px;
`;

const RadioLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 15px !important;
    font-weight: 500 !important;
    color: #333 !important;
    padding: 10px 20px;
    border: 2px solid ${props => props.selected ? 'rgb(24, 51, 189)' : '#ddd'};
    border-radius: 8px;
    background: ${props => props.selected ? '#f0f4ff' : 'white'};
    transition: all 0.2s;

    &:hover {
        border-color: rgb(24, 51, 189);
    }

    input[type="radio"] {
        accent-color: rgb(24, 51, 189);
        width: 16px;
        height: 16px;
    }
`;

const DropZone = styled.div`
    border: 2px dashed ${props => props.isDragOver ? 'rgb(24, 51, 189)' : '#ccc'};
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    color: #888;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    background: ${props => props.isDragOver ? '#f0f4ff' : '#fafafa'};
    margin-bottom: 12px;

    &:hover {
        border-color: rgb(24, 51, 189);
        background: #f0f4ff;
    }
`;

const PreviewGrid = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const PreviewItem = styled.div`
    position: relative;
    width: 90px;
    height: 90px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid #ddd;
    }

    .remove-btn {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #c0392b;
        color: white;
        border: none;
        font-size: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
    }
`;

const FileItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    margin-bottom: 8px;

    .file-name {
        flex: 1;
        font-size: 13px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .file-size {
        font-size: 12px;
        color: #888;
        white-space: nowrap;
    }

    .remove-btn {
        background: #c0392b;
        color: white;
        border: none;
        border-radius: 50%;
        width: 22px;
        height: 22px;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        flex-shrink: 0;
    }
`;

const SubmitBtn = styled.button`
    width: 100%;
    padding: 14px 24px;
    background: rgb(24, 51, 189);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 10px;

    &:hover:not(:disabled) {
        background: #1a237e;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Alert = styled.div`
    padding: 16px 20px;
    border-radius: 8px;
    font-size: 15px;
    margin-bottom: 20px;

    ${props => props.variant === 'success' && `
        background-color: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
    `}

    ${props => props.variant === 'danger' && `
        background-color: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
    `}

    ${props => props.variant === 'info' && `
        background-color: #e8f0fe;
        color: #1a3b6b;
        border: 1px solid #d0ddf0;
    `}
`;

const ErrorText = styled.span`
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
`;

// ─── Component ───────────────────────────────────────────────
const FD100ExpoDisponibiliteForm = () => {
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('project');
    const { i18n } = useTranslation();
    const isFr = i18n.language !== 'en';

    // Loading / error states
    const [loading, setLoading] = useState(true);
    const [designerName, setDesignerName] = useState('');
    const [designerEmail, setDesignerEmail] = useState('');
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [errors, setErrors] = useState({});

    // Form state
    const [form, setForm] = useState({
        disponibilite: '',
        nom_piece: '',
        typologie: '',
        date_creation: '',
        designer_names: '',
        editeur: '',
        materiaux: '',
        dimensions: '',
        poids: '',
        valeur_assurance: '',
        contact_prenom: '',
        contact_nom: '',
        contact_email: '',
        contact_telephone: '',
    });

    // File states
    const [photos, setPhotos] = useState([]);
    const [files3d, setFiles3d] = useState([]);
    const [photoDragOver, setPhotoDragOver] = useState(false);
    const [file3dDragOver, setFile3dDragOver] = useState(false);
    const [existingData, setExistingData] = useState(null);

    // Multi-piece mode (Sam Baron)
    const isMultiPiece = MULTI_PIECE_PROJECT_IDS.includes(projectId);
    const [existingPieces, setExistingPieces] = useState([]);
    const [addingPiece, setAddingPiece] = useState(false);

    const photoInputRef = useRef(null);
    const file3dInputRef = useRef(null);

    // ─── Load project info ──────────────────────────────────
    useEffect(() => {
        if (!projectId) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        const loadData = async () => {
            try {
                // Fetch designer name
                const { data: nameData } = await supabase
                    .from('fd100_project_names')
                    .select('designer_definitive')
                    .eq('project_id', projectId)
                    .eq('event_id', EVENT_ID)
                    .single();

                // Fetch designer email from laureats_tracking
                const { data: trackingData } = await supabase
                    .from('fd100_laureats_tracking')
                    .select('user_email, user_name')
                    .eq('project_id', projectId)
                    .eq('event_id', EVENT_ID)
                    .single();

                if (!nameData && !trackingData) {
                    setNotFound(true);
                    setLoading(false);
                    return;
                }

                const designer = nameData?.designer_definitive || trackingData?.user_name || '';
                setDesignerName(designer);
                setDesignerEmail(trackingData?.user_email || '');

                // Check if already submitted
                const { data: existing } = await supabase
                    .from('fd100_expo_disponibilite')
                    .select('*')
                    .eq('project_id', projectId)
                    .eq('event_id', EVENT_ID)
                    .single();

                if (existing) {
                    setExistingData(existing);
                    if (MULTI_PIECE_PROJECT_IDS.includes(projectId)) {
                        // Multi-piece: only keep contact info & designer, leave piece fields blank
                        setForm(f => ({
                            ...f,
                            designer_names: existing.designer_names || designer,
                            contact_prenom: existing.contact_prenom || '',
                            contact_nom: existing.contact_nom || '',
                            contact_email: existing.contact_email || trackingData?.user_email || '',
                            contact_telephone: existing.contact_telephone || '',
                        }));
                    } else {
                        setForm({
                            disponibilite: existing.disponibilite || '',
                            nom_piece: existing.nom_piece || '',
                            typologie: existing.typologie || '',
                            date_creation: existing.date_creation || '',
                            designer_names: existing.designer_names || '',
                            editeur: existing.editeur || '',
                            materiaux: existing.materiaux || '',
                            dimensions: existing.dimensions || '',
                            poids: existing.poids || '',
                            valeur_assurance: existing.valeur_assurance || '',
                            contact_prenom: existing.contact_prenom || '',
                            contact_nom: existing.contact_nom || '',
                            contact_email: existing.contact_email || trackingData?.user_email || '',
                            contact_telephone: existing.contact_telephone || '',
                        });
                    }
                } else {
                    // Pre-fill with known data
                    setForm(f => ({
                        ...f,
                        designer_names: designer,
                        contact_email: trackingData?.user_email || '',
                    }));
                }

                // Multi-piece mode: fetch existing submitted pieces
                if (MULTI_PIECE_PROJECT_IDS.includes(projectId)) {
                    const { data: pieces } = await supabase
                        .from('fd100_exposition_pieces')
                        .select('*')
                        .eq('project_id', projectId)
                        .eq('event_id', EVENT_ID)
                        .order('created_at', { ascending: true });
                    setExistingPieces(pieces || []);
                }

                setLoading(false);
            } catch (err) {
                console.error('Error loading data:', err);
                setNotFound(true);
                setLoading(false);
            }
        };

        loadData();
    }, [projectId]);

    // ─── Field change handler ───────────────────────────────
    const handleChange = useCallback((field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
    }, [errors]);

    // ─── Photo handlers ─────────────────────────────────────
    const handlePhotoDrop = useCallback((e) => {
        e.preventDefault();
        setPhotoDragOver(false);
        const files = Array.from(e.dataTransfer?.files || e.target?.files || [])
            .filter(f => f.type.startsWith('image/'));
        if (files.length) setPhotos(prev => [...prev, ...files]);
    }, []);

    const removePhoto = useCallback((idx) => {
        setPhotos(prev => prev.filter((_, i) => i !== idx));
    }, []);

    // ─── 3D file handlers ───────────────────────────────────
    const handle3dDrop = useCallback((e) => {
        e.preventDefault();
        setFile3dDragOver(false);
        const accepted = ['.obj', '.fbx'];
        const files = Array.from(e.dataTransfer?.files || e.target?.files || [])
            .filter(f => {
                const ext = '.' + f.name.split('.').pop().toLowerCase();
                return accepted.includes(ext);
            });
        if (files.length) setFiles3d(prev => [...prev, ...files]);
    }, []);

    const remove3dFile = useCallback((idx) => {
        setFiles3d(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // ─── Validation ─────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (isMultiPiece) {
            if (!form.nom_piece) errs.nom_piece = isFr ? 'Champ requis' : 'Required';
            if (!form.dimensions) errs.dimensions = isFr ? 'Champ requis' : 'Required';
            if (!form.materiaux) errs.materiaux = isFr ? 'Champ requis' : 'Required';
        } else {
            if (!form.disponibilite) errs.disponibilite = isFr ? 'Champ requis' : 'Required';
            if (form.disponibilite === 'oui') {
                if (!form.nom_piece) errs.nom_piece = isFr ? 'Champ requis' : 'Required';
                if (!form.dimensions) errs.dimensions = isFr ? 'Champ requis' : 'Required';
                if (!form.materiaux) errs.materiaux = isFr ? 'Champ requis' : 'Required';
            }
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ─── Submit ─────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validate()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setSubmitting(true);
        setSubmitError('');

        try {
            // Upload photos — for multi-piece, start fresh (each piece has own photos)
            // For single-piece, keep existing photos from dispo row
            const uploadedPhotoUrls = isMultiPiece ? [] : (existingData?.photo_urls || []);
            for (const file of photos) {
                const ext = file.name.split('.').pop().toLowerCase();
                const fileName = `disponibilite/${projectId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
                const { error: upErr } = await supabase.storage
                    .from('fd100-exposition-photos')
                    .upload(fileName, file);
                if (upErr) { console.error('Photo upload error:', upErr); continue; }
                const { data: { publicUrl } } = supabase.storage
                    .from('fd100-exposition-photos')
                    .getPublicUrl(fileName);
                uploadedPhotoUrls.push(publicUrl);
            }

            // Upload 3D files — same logic: multi-piece starts fresh
            const uploaded3dUrls = isMultiPiece ? [] : (existingData?.file_3d_urls || []);
            for (const file of files3d) {
                const ext = file.name.split('.').pop().toLowerCase();
                const fileName = `disponibilite/${projectId}/3d_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
                const blob = new Blob([file], { type: 'application/octet-stream' });
                const { error: upErr } = await supabase.storage
                    .from('fd100-exposition-photos')
                    .upload(fileName, blob, { contentType: 'application/octet-stream' });
                if (upErr) { console.error('3D file upload error:', upErr); continue; }
                const { data: { publicUrl } } = supabase.storage
                    .from('fd100-exposition-photos')
                    .getPublicUrl(fileName);
                uploaded3dUrls.push(publicUrl);
            }

            if (isMultiPiece) {
                // Multi-piece: insert into fd100_exposition_pieces
                const pieceRow = {
                    event_id: EVENT_ID,
                    project_id: projectId,
                    nom_piece: form.nom_piece,
                    typologie: form.typologie,
                    date_creation: form.date_creation,
                    designer_names: form.designer_names,
                    editeur: form.editeur,
                    fabricant: form.editeur,
                    materiaux: form.materiaux,
                    dimensions: form.dimensions,
                    poids: form.poids,
                    valeur_assurance: form.valeur_assurance,
                    photo_urls: uploadedPhotoUrls,
                    file_3d_urls: uploaded3dUrls.length > 0 ? uploaded3dUrls : [],
                    selection_finale: 'oui',
                    updated_at: new Date().toISOString(),
                };
                const { data: newPiece, error } = await supabase
                    .from('fd100_exposition_pieces')
                    .insert(pieceRow)
                    .select()
                    .single();
                if (error) throw error;

                // Add to list, reset form for next piece
                setExistingPieces(prev => [...prev, newPiece]);
                setForm(f => ({
                    ...f,
                    nom_piece: '', typologie: '', date_creation: '',
                    editeur: '', materiaux: '', dimensions: '',
                    poids: '', valeur_assurance: '',
                }));
                setPhotos([]);
                setFiles3d([]);
                setAddingPiece(false);
                setSubmitted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                const row = {
                    event_id: EVENT_ID,
                    project_id: projectId,
                    disponibilite: form.disponibilite,
                    nom_piece: form.nom_piece,
                    typologie: form.typologie,
                    date_creation: form.date_creation,
                    designer_names: form.designer_names,
                    editeur: form.editeur,
                    materiaux: form.materiaux,
                    dimensions: form.dimensions,
                    poids: form.poids,
                    valeur_assurance: form.valeur_assurance,
                    photo_urls: uploadedPhotoUrls,
                    file_3d_urls: uploaded3dUrls,
                    contact_prenom: form.contact_prenom,
                    contact_nom: form.contact_nom,
                    contact_email: form.contact_email,
                    contact_telephone: form.contact_telephone,
                    updated_at: new Date().toISOString(),
                };

                if (existingData) {
                    const { error } = await supabase
                        .from('fd100_expo_disponibilite')
                        .update(row)
                        .eq('id', existingData.id);
                    if (error) throw error;
                } else {
                    const { error } = await supabase
                        .from('fd100_expo_disponibilite')
                        .insert(row);
                    if (error) throw error;
                }

                setSubmitted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            console.error('Submit error:', err);
            setSubmitError(isFr
                ? 'Une erreur est survenue. Veuillez réessayer.'
                : 'An error occurred. Please try again.');
        }
        setSubmitting(false);
    };

    // ─── Render ─────────────────────────────────────────────
    if (loading) {
        return (
            <PageContainer>
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spinner />
                </div>
            </PageContainer>
        );
    }

    if (notFound || !projectId) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <Alert variant="danger">
                    {isFr
                        ? 'Lien invalide ou projet introuvable.'
                        : 'Invalid link or project not found.'}
                </Alert>
            </PageContainer>
        );
    }

    // ─── Multi-piece render (Sam Baron) ─────────────────────
    if (isMultiPiece) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <PageTitle>
                    {isFr
                        ? 'Exposition Le FD100 — Soumettre vos pièces'
                        : 'Le FD100 Exhibition — Submit your pieces'}
                </PageTitle>
                <PageSubtitle>
                    {isFr
                        ? 'Galerie Le FRENCH DESIGN — 120 avenue Ledru Rollin, 75011 Paris'
                        : 'Le FRENCH DESIGN Gallery — 120 avenue Ledru Rollin, 75011 Paris'}
                </PageSubtitle>

                {designerName && <DesignerBadge>{designerName}</DesignerBadge>}

                {submitted && (
                    <Alert variant="success" style={{ textAlign: 'center', marginBottom: 20 }}>
                        {isFr
                            ? 'Pièce ajoutée avec succès !'
                            : 'Piece added successfully!'}
                    </Alert>
                )}

                {/* Existing pieces list */}
                {existingPieces.length > 0 && (
                    <SectionCard>
                        <SectionTitle>
                            {isFr ? `Pièces soumises (${existingPieces.length})` : `Submitted pieces (${existingPieces.length})`}
                        </SectionTitle>
                        {existingPieces.map((p, i) => (
                            <div key={p.id} style={{
                                display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0',
                                borderBottom: i < existingPieces.length - 1 ? '1px solid #eee' : 'none'
                            }}>
                                {p.photo_urls?.[0] ? (
                                    <img src={p.photo_urls[0]} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
                                ) : (
                                    <div style={{ width: 60, height: 60, background: '#f0f0f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>—</div>
                                )}
                                <div>
                                    <div style={{ fontWeight: 600 }}>{p.nom_piece || '—'}</div>
                                    <div style={{ fontSize: 13, color: '#888' }}>
                                        {[p.typologie, p.materiaux, p.dimensions].filter(Boolean).join(' — ')}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </SectionCard>
                )}

                {/* Add piece form */}
                {!addingPiece ? (
                    <SubmitBtn onClick={() => {
                        setForm(f => ({
                            ...f,
                            nom_piece: '', typologie: '', date_creation: '',
                            editeur: '', materiaux: '', dimensions: '',
                            poids: '', valeur_assurance: '',
                        }));
                        setPhotos([]);
                        setFiles3d([]);
                        setErrors({});
                        setAddingPiece(true);
                        setSubmitted(false);
                    }} style={{ width: '100%' }}>
                        {isFr ? '+ Ajouter une pièce' : '+ Add a piece'}
                    </SubmitBtn>
                ) : (
                    <>
                        {submitError && <Alert variant="danger">{submitError}</Alert>}
                        <SectionCard>
                            <SectionTitle>
                                {isFr ? 'Nouvelle pièce' : 'New piece'}
                            </SectionTitle>

                            <FormGroup>
                                <label>{isFr ? 'Nom de la pièce' : 'Piece name'}<RequiredMark>*</RequiredMark></label>
                                <input
                                    value={form.nom_piece}
                                    onChange={e => handleChange('nom_piece', e.target.value)}
                                    placeholder={isFr ? 'Ex: Chaise Longue LC4' : 'Ex: LC4 Chaise Longue'}
                                    style={{ borderColor: errors.nom_piece ? '#dc3545' : '' }}
                                />
                                {errors.nom_piece && <ErrorText>{errors.nom_piece}</ErrorText>}
                            </FormGroup>

                            <FormRow>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Typologie du mobilier' : 'Furniture typology'}</label>
                                    <input value={form.typologie} onChange={e => handleChange('typologie', e.target.value)} placeholder={isFr ? 'Ex: Chaise, Table...' : 'Ex: Chair, Table...'} />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Date de création' : 'Creation date'}</label>
                                    <input value={form.date_creation} onChange={e => handleChange('date_creation', e.target.value)} placeholder="Ex: 2024" />
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Nom(s) du ou des designer(s)' : 'Designer name(s)'}</label>
                                    <input value={form.designer_names} onChange={e => handleChange('designer_names', e.target.value)} />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Nom du fabricant / éditeur' : 'Manufacturer / Publisher'}</label>
                                    <input value={form.editeur} onChange={e => handleChange('editeur', e.target.value)} />
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Dimensions' : 'Dimensions'}<RequiredMark>*</RequiredMark></label>
                                    <input
                                        value={form.dimensions}
                                        onChange={e => handleChange('dimensions', e.target.value)}
                                        placeholder="Ex: L120 x P60 x H75 cm"
                                        style={{ borderColor: errors.dimensions ? '#dc3545' : '' }}
                                    />
                                    {errors.dimensions && <ErrorText>{errors.dimensions}</ErrorText>}
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Matériaux' : 'Materials'}<RequiredMark>*</RequiredMark></label>
                                    <input
                                        value={form.materiaux}
                                        onChange={e => handleChange('materiaux', e.target.value)}
                                        placeholder={isFr ? 'Ex: Bois, Métal...' : 'Ex: Wood, Metal...'}
                                        style={{ borderColor: errors.materiaux ? '#dc3545' : '' }}
                                    />
                                    {errors.materiaux && <ErrorText>{errors.materiaux}</ErrorText>}
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? 'Poids de la pièce' : 'Piece weight'}</label>
                                    <input
                                        value={form.poids}
                                        onChange={e => handleChange('poids', e.target.value)}
                                        placeholder={isFr ? 'Ex: 5 kg' : 'Ex: 5 kg'}
                                    />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0 }}>
                                    <label>{isFr ? "Valeur d'assurance" : 'Insurance value'}</label>
                                    <input
                                        value={form.valeur_assurance}
                                        onChange={e => handleChange('valeur_assurance', e.target.value)}
                                        placeholder={isFr ? 'Ex: 5 000 €' : 'Ex: €5,000'}
                                    />
                                </FormGroup>
                            </FormRow>
                        </SectionCard>

                        {/* Photos */}
                        <SectionCard>
                            <SectionTitle>{isFr ? 'Visuels de la pièce' : 'Piece visuals'}</SectionTitle>
                            <DropZone
                                isDragOver={photoDragOver}
                                onDragOver={e => { e.preventDefault(); setPhotoDragOver(true); }}
                                onDragLeave={() => setPhotoDragOver(false)}
                                onDrop={handlePhotoDrop}
                                onClick={() => photoInputRef.current?.click()}
                            >
                                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: 'none' }} onChange={handlePhotoDrop} />
                                {isFr ? 'Glissez vos photos ici ou cliquez pour sélectionner' : 'Drag photos here or click to select'}
                                <br /><span style={{ fontSize: 12, color: '#aaa' }}>JPEG, PNG, WebP</span>
                            </DropZone>
                            {photos.length > 0 && (
                                <PreviewGrid>
                                    {photos.map((file, i) => (
                                        <PreviewItem key={i}>
                                            <img src={URL.createObjectURL(file)} alt="" />
                                            <button className="remove-btn" onClick={() => removePhoto(i)}>&times;</button>
                                        </PreviewItem>
                                    ))}
                                </PreviewGrid>
                            )}
                        </SectionCard>

                        {/* 3D Files */}
                        <SectionCard>
                            <SectionTitle>{isFr ? 'Fichiers 3D' : '3D Files'}</SectionTitle>
                            <DropZone
                                isDragOver={file3dDragOver}
                                onDragOver={e => { e.preventDefault(); setFile3dDragOver(true); }}
                                onDragLeave={() => setFile3dDragOver(false)}
                                onDrop={handle3dDrop}
                                onClick={() => file3dInputRef.current?.click()}
                            >
                                <input ref={file3dInputRef} type="file" accept=".obj,.fbx" multiple style={{ display: 'none' }} onChange={handle3dDrop} />
                                {isFr ? 'Glissez vos fichiers 3D ici ou cliquez pour sélectionner' : 'Drag 3D files here or click to select'}
                                <br /><span style={{ fontSize: 12, color: '#aaa' }}>OBJ, FBX</span>
                            </DropZone>
                            {files3d.map((file, i) => (
                                <FileItem key={`new-3d-${i}`}>
                                    <span style={{ fontSize: 20 }}>&#128451;</span>
                                    <span className="file-name">{file.name}</span>
                                    <span className="file-size">{formatFileSize(file.size)}</span>
                                    <button className="remove-btn" onClick={() => remove3dFile(i)}>&times;</button>
                                </FileItem>
                            ))}
                        </SectionCard>

                        <div style={{ display: 'flex', gap: 12 }}>
                            <SubmitBtn onClick={handleSubmit} disabled={submitting} style={{ flex: 1 }}>
                                {submitting ? (isFr ? 'Envoi...' : 'Submitting...') : (isFr ? 'Ajouter la pièce' : 'Add piece')}
                            </SubmitBtn>
                            <SubmitBtn onClick={() => { setAddingPiece(false); setErrors({}); }} style={{ flex: 0, background: '#ccc', color: '#333', minWidth: 120 }}>
                                {isFr ? 'Annuler' : 'Cancel'}
                            </SubmitBtn>
                        </div>
                    </>
                )}
            </PageContainer>
        );
    }

    // ─── Standard single-piece render ────────────────────────
    if (submitted) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <Alert variant="success" style={{ textAlign: 'center', fontSize: '16px' }}>
                    {form.disponibilite === 'oui'
                        ? (isFr
                            ? 'Merci ! Vos informations ont bien été enregistrées. Nous reviendrons vers vous prochainement.'
                            : 'Thank you! Your information has been saved. We will get back to you shortly.')
                        : (isFr
                            ? 'Merci pour votre retour. Nous avons bien noté que la pièce n\'est pas disponible.'
                            : 'Thank you for your response. We have noted that the piece is not available.')}
                </Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
            <PageTitle>
                {isFr
                    ? 'Exposition Le FD100 — Disponibilité de votre pièce'
                    : 'Le FD100 Exhibition — Piece Availability'}
            </PageTitle>
            <PageSubtitle>
                {isFr
                    ? 'Galerie Le FRENCH DESIGN — 120 avenue Ledru Rollin, 75011 Paris'
                    : 'Le FRENCH DESIGN Gallery — 120 avenue Ledru Rollin, 75011 Paris'}
            </PageSubtitle>

            {designerName && <DesignerBadge>{designerName}</DesignerBadge>}

            {existingData && (
                <Alert variant="info">
                    {isFr
                        ? 'Vous avez déjà répondu à ce formulaire. Vous pouvez modifier vos réponses ci-dessous.'
                        : 'You have already submitted this form. You can update your answers below.'}
                </Alert>
            )}

            {submitError && <Alert variant="danger">{submitError}</Alert>}

            {/* ── Section 1: Disponibilité ── */}
            <SectionCard>
                <SectionTitle>
                    {isFr ? 'Disponibilité de la pièce' : 'Piece availability'}
                </SectionTitle>
                <FormGroup>
                    <label>
                        {isFr
                            ? 'Votre pièce est-elle disponible pour la période du 15 octobre au mois de février ?'
                            : 'Is your piece available for the period from October 15 to February?'}
                        <RequiredMark>*</RequiredMark>
                    </label>
                    <RadioGroup>
                        <RadioLabel selected={form.disponibilite === 'oui'}>
                            <input
                                type="radio"
                                name="disponibilite"
                                value="oui"
                                checked={form.disponibilite === 'oui'}
                                onChange={() => handleChange('disponibilite', 'oui')}
                            />
                            {isFr ? 'Oui' : 'Yes'}
                        </RadioLabel>
                        <RadioLabel selected={form.disponibilite === 'non'}>
                            <input
                                type="radio"
                                name="disponibilite"
                                value="non"
                                checked={form.disponibilite === 'non'}
                                onChange={() => handleChange('disponibilite', 'non')}
                            />
                            {isFr ? 'Non' : 'No'}
                        </RadioLabel>
                    </RadioGroup>
                    {errors.disponibilite && <ErrorText>{errors.disponibilite}</ErrorText>}
                </FormGroup>
            </SectionCard>

            {/* ── Section 2: Piece info (only if available) ── */}
            {form.disponibilite === 'oui' && (
                <>
                    <SectionCard>
                        <SectionTitle>
                            {isFr ? 'Informations sur la pièce' : 'Piece information'}
                        </SectionTitle>

                        <FormGroup>
                            <label>{isFr ? 'Nom de la pièce' : 'Piece name'}<RequiredMark>*</RequiredMark></label>
                            <input
                                value={form.nom_piece}
                                onChange={e => handleChange('nom_piece', e.target.value)}
                                placeholder={isFr ? 'Ex: Chaise Longue LC4' : 'Ex: LC4 Chaise Longue'}
                                style={{ borderColor: errors.nom_piece ? '#dc3545' : '' }}
                            />
                            {errors.nom_piece && <ErrorText>{errors.nom_piece}</ErrorText>}
                        </FormGroup>

                        <FormRow>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Typologie du mobilier' : 'Furniture typology'}</label>
                                <input
                                    value={form.typologie}
                                    onChange={e => handleChange('typologie', e.target.value)}
                                    placeholder={isFr ? 'Ex: Chaise, Table, Luminaire...' : 'Ex: Chair, Table, Lamp...'}
                                />
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Date de création' : 'Creation date'}</label>
                                <input
                                    value={form.date_creation}
                                    onChange={e => handleChange('date_creation', e.target.value)}
                                    placeholder={isFr ? 'Ex: 2024' : 'Ex: 2024'}
                                />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Nom(s) du ou des designer(s)' : 'Designer name(s)'}</label>
                                <input
                                    value={form.designer_names}
                                    onChange={e => handleChange('designer_names', e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Nom du fabricant / éditeur' : 'Manufacturer / Publisher name'}</label>
                                <input
                                    value={form.editeur}
                                    onChange={e => handleChange('editeur', e.target.value)}
                                />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Dimensions de la pièce' : 'Piece dimensions'}<RequiredMark>*</RequiredMark></label>
                                <input
                                    value={form.dimensions}
                                    onChange={e => handleChange('dimensions', e.target.value)}
                                    placeholder={isFr ? 'Ex: L120 x P60 x H75 cm' : 'Ex: W120 x D60 x H75 cm'}
                                    style={{ borderColor: errors.dimensions ? '#dc3545' : '' }}
                                />
                                {errors.dimensions && <ErrorText>{errors.dimensions}</ErrorText>}
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Poids de la pièce' : 'Piece weight'}</label>
                                <input
                                    value={form.poids}
                                    onChange={e => handleChange('poids', e.target.value)}
                                    placeholder={isFr ? 'Ex: 5 kg' : 'Ex: 5 kg'}
                                />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? 'Matériaux de la pièce' : 'Piece materials'}<RequiredMark>*</RequiredMark></label>
                                <input
                                    value={form.materiaux}
                                    onChange={e => handleChange('materiaux', e.target.value)}
                                    placeholder={isFr ? 'Ex: Bois, Métal, Tissu...' : 'Ex: Wood, Metal, Fabric...'}
                                    style={{ borderColor: errors.materiaux ? '#dc3545' : '' }}
                                />
                                {errors.materiaux && <ErrorText>{errors.materiaux}</ErrorText>}
                            </FormGroup>
                            <FormGroup style={{ marginBottom: 0 }}>
                                <label>{isFr ? "Valeur d'assurance de la pièce" : 'Piece insurance value'}</label>
                                <input
                                    value={form.valeur_assurance}
                                    onChange={e => handleChange('valeur_assurance', e.target.value)}
                                    placeholder={isFr ? 'Ex: 5 000 €' : 'Ex: €5,000'}
                                />
                            </FormGroup>
                        </FormRow>
                    </SectionCard>

                    {/* ── Section 3: Photos ── */}
                    <SectionCard>
                        <SectionTitle>
                            {isFr ? 'Visuels de la pièce' : 'Piece visuals'}
                        </SectionTitle>

                        <DropZone
                            isDragOver={photoDragOver}
                            onDragOver={e => { e.preventDefault(); setPhotoDragOver(true); }}
                            onDragLeave={() => setPhotoDragOver(false)}
                            onDrop={handlePhotoDrop}
                            onClick={() => photoInputRef.current?.click()}
                        >
                            <input
                                ref={photoInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handlePhotoDrop}
                            />
                            {isFr
                                ? 'Glissez vos photos ici ou cliquez pour sélectionner'
                                : 'Drag photos here or click to select'}
                            <br />
                            <span style={{ fontSize: 12, color: '#aaa' }}>JPEG, PNG, WebP</span>
                        </DropZone>

                        {/* Existing photos (edit mode) */}
                        {existingData?.photo_urls?.length > 0 && (
                            <PreviewGrid>
                                {existingData.photo_urls.map((url, i) => (
                                    <PreviewItem key={`existing-${i}`}>
                                        <img src={url} alt="" />
                                    </PreviewItem>
                                ))}
                            </PreviewGrid>
                        )}

                        {/* New photos preview */}
                        {photos.length > 0 && (
                            <PreviewGrid>
                                {photos.map((file, i) => (
                                    <PreviewItem key={`new-${i}`}>
                                        <img src={URL.createObjectURL(file)} alt="" />
                                        <button className="remove-btn" onClick={() => removePhoto(i)}>&times;</button>
                                    </PreviewItem>
                                ))}
                            </PreviewGrid>
                        )}
                    </SectionCard>

                    {/* ── Section 4: 3D Files ── */}
                    <SectionCard>
                        <SectionTitle>
                            {isFr ? 'Fichiers 3D' : '3D Files'}
                        </SectionTitle>

                        <DropZone
                            isDragOver={file3dDragOver}
                            onDragOver={e => { e.preventDefault(); setFile3dDragOver(true); }}
                            onDragLeave={() => setFile3dDragOver(false)}
                            onDrop={handle3dDrop}
                            onClick={() => file3dInputRef.current?.click()}
                        >
                            <input
                                ref={file3dInputRef}
                                type="file"
                                accept=".obj,.fbx"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handle3dDrop}
                            />
                            {isFr
                                ? 'Glissez vos fichiers 3D ici ou cliquez pour sélectionner'
                                : 'Drag 3D files here or click to select'}
                            <br />
                            <span style={{ fontSize: 12, color: '#aaa' }}>OBJ, FBX</span>
                        </DropZone>

                        {/* Existing 3D files (edit mode) */}
                        {existingData?.file_3d_urls?.length > 0 && existingData.file_3d_urls.map((url, i) => (
                            <FileItem key={`existing-3d-${i}`}>
                                <span style={{ fontSize: 20 }}>&#128451;</span>
                                <span className="file-name">{url.split('/').pop()}</span>
                            </FileItem>
                        ))}

                        {/* New 3D files */}
                        {files3d.map((file, i) => (
                            <FileItem key={`new-3d-${i}`}>
                                <span style={{ fontSize: 20 }}>&#128451;</span>
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">{formatFileSize(file.size)}</span>
                                <button className="remove-btn" onClick={() => remove3dFile(i)}>&times;</button>
                            </FileItem>
                        ))}
                    </SectionCard>
                </>
            )}

            {/* ── Section 5: Contact ── */}
            {form.disponibilite && (
                <SectionCard>
                    <SectionTitle>
                        {isFr ? 'Informations de contact' : 'Contact information'}
                    </SectionTitle>

                    <FormRow>
                        <FormGroup style={{ marginBottom: 0 }}>
                            <label>{isFr ? 'Prénom' : 'First name'}</label>
                            <input
                                value={form.contact_prenom}
                                onChange={e => handleChange('contact_prenom', e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup style={{ marginBottom: 0 }}>
                            <label>{isFr ? 'Nom' : 'Last name'}</label>
                            <input
                                value={form.contact_nom}
                                onChange={e => handleChange('contact_nom', e.target.value)}
                            />
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup style={{ marginBottom: 0 }}>
                            <label>Email</label>
                            <input
                                type="email"
                                value={form.contact_email}
                                onChange={e => handleChange('contact_email', e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup style={{ marginBottom: 0 }}>
                            <label>{isFr ? 'Téléphone' : 'Phone'}</label>
                            <input
                                type="tel"
                                value={form.contact_telephone}
                                onChange={e => handleChange('contact_telephone', e.target.value)}
                            />
                        </FormGroup>
                    </FormRow>
                </SectionCard>
            )}

            {/* ── Submit ── */}
            {form.disponibilite && (
                <SubmitBtn onClick={handleSubmit} disabled={submitting}>
                    {submitting
                        ? (isFr ? 'Envoi en cours...' : 'Submitting...')
                        : existingData
                            ? (isFr ? 'Mettre à jour' : 'Update')
                            : (isFr ? 'Envoyer' : 'Submit')}
                </SubmitBtn>
            )}
        </PageContainer>
    );
};

export default FD100ExpoDisponibiliteForm;
